<template>
  <section>
    <div class="guests-page__head" style="margin-bottom: 14px">
      <h2 class="guests-page__title" style="font-size: 20px">Strutture</h2>
      <button class="villa-btn villa-btn--primary" @click="openCreate">
        <i class="fa-solid fa-plus"></i> Crea nuova struttura
      </button>
    </div>

    <div v-if="loading" class="guests-loading">Caricamento strutture…</div>
    <div v-else-if="error" class="guests-loading">{{ error }}</div>
    <div v-else-if="facilities.length === 0" class="guests-loading">
      Nessuna struttura. Crea la prima struttura.
    </div>

    <div v-else class="facilities-grid">
      <div v-for="f in facilities" :key="f.id" class="facility-card">
        <div>
          <h3 class="facility-card__title">{{ f.name }}</h3>
          <div class="facility-card__type">
            {{ typeLabel(f.type) }}
            <span
              :class="[
                'facility-badge',
                f.isActive
                  ? 'facility-badge--active'
                  : 'facility-badge--inactive',
              ]"
            >
              {{ f.isActive ? "Attiva" : "Non attiva" }}
            </span>
          </div>
        </div>
        <div class="facility-card__meta">
          <span v-if="f.city">
            <i class="fa-solid fa-location-dot"></i>
            {{ f.address ? f.address + " — " : "" }}{{ f.city
            }}<template v-if="f.province"> ({{ f.province }})</template>
          </span>
          <span><strong>Capienza:</strong> {{ f.maxCapacity }} posti</span>
          <span
            ><strong>Occupazione:</strong> {{ occ(f).occupiedBeds || 0 }} /
            {{ occ(f).totalBeds || 0 }} posti ·
            {{ occ(f).occupancyRate || 0 }}%</span
          >
          <template v-if="canViewEconomics">
            <span
              ><strong>Rendita attuale:</strong>
              {{ euro(eco(f).currentMonthlyRevenue) }}/mese</span
            >
            <span
              ><strong>Rendita potenziale:</strong>
              {{ euro(eco(f).potentialMonthlyRevenue) }}/mese</span
            >
            <span
              ><strong>Gap:</strong> {{ euro(eco(f).revenueGap) }}/mese</span
            >
            <span
              ><strong>Retta media attesa:</strong>
              {{ euro(f.expectedMonthlyFee) }}/mese</span
            >
          </template>
        </div>
        <div class="facility-card__actions">
          <button class="villa-btn villa-btn--primary" @click="openFacility(f)">
            <i class="fa-solid fa-arrow-right-to-bracket"></i> Apri struttura
          </button>
          <button class="villa-btn villa-btn--ghost" @click="openEdit(f)">
            <i class="fa-solid fa-pen-to-square"></i> Modifica
          </button>
          <button class="villa-btn villa-btn--danger" @click="askArchive(f)">
            <i class="fa-solid fa-box-archive"></i> Archivia
          </button>
        </div>
      </div>
    </div>

    <!-- Modale crea/modifica struttura -->
    <VueFinalModal
      :model-value="modal.open"
      content-class="villa-modal villa-modal--wide"
      @update:model-value="onModalToggle"
    >
      <h3 class="villa-modal__title">
        {{ modal.mode === "edit" ? "Modifica struttura" : "Nuova struttura" }}
      </h3>
      <div class="form-grid">
        <div class="form-field form-field--full">
          <label>Nome struttura <span class="required">*</span></label>
          <input v-model.trim="form.name" type="text" />
        </div>
        <div class="form-field">
          <label>Tipologia</label>
          <select v-model="form.type">
            <option v-for="t in types" :key="t.value" :value="t.value">
              {{ t.label }}
            </option>
          </select>
        </div>
        <div class="form-field">
          <label>Attiva</label>
          <select v-model="form.isActive">
            <option :value="true">Sì</option>
            <option :value="false">No</option>
          </select>
        </div>
        <div class="form-field form-field--full">
          <label>Ragione sociale</label>
          <input v-model.trim="form.legalName" type="text" />
        </div>
        <div class="form-field form-field--full">
          <label>Indirizzo</label>
          <input v-model.trim="form.address" type="text" />
        </div>
        <div class="form-field">
          <label>Città</label>
          <input v-model.trim="form.city" type="text" />
        </div>
        <div class="form-field">
          <label>Provincia</label>
          <input v-model.trim="form.province" type="text" />
        </div>
        <div class="form-field">
          <label>CAP</label>
          <input v-model.trim="form.cap" type="text" />
        </div>
        <div class="form-field">
          <label>Telefono</label>
          <input v-model.trim="form.phone" type="text" />
        </div>
        <div class="form-field">
          <label>Email</label>
          <input v-model.trim="form.email" type="text" />
        </div>
        <div class="form-field">
          <label>PEC</label>
          <input v-model.trim="form.pec" type="text" />
        </div>
        <div class="form-field">
          <label>Codice fiscale</label>
          <input v-model.trim="form.taxCode" type="text" />
        </div>
        <div class="form-field">
          <label>Partita IVA</label>
          <input v-model.trim="form.vatNumber" type="text" />
        </div>
        <div class="form-field">
          <label>Codice univoco</label>
          <input v-model.trim="form.codiceUnivoco" type="text" />
        </div>
        <div v-if="canViewCompany" class="form-field">
          <label>Codice struttura (documenti)</label>
          <input
            v-model.trim="form.code"
            type="text"
            maxlength="10"
            placeholder="es. LF, VM — se vuoto si deriva dal nome"
          />
        </div>
        <div class="form-field">
          <label>Capienza massima</label>
          <input v-model="form.maxCapacity" type="number" min="0" step="1" />
        </div>
        <div class="form-field">
          <label>Retta mensile attesa (€)</label>
          <input
            v-model="form.expectedMonthlyFee"
            type="number"
            min="0"
            step="any"
          />
        </div>
        <!-- Numero camere: solo in creazione. Alla creazione genera le camere
             vuote corrispondenti (Camera 1..N). -->
        <div v-if="modal.mode === 'create'" class="form-field">
          <label>Numero camere</label>
          <input
            v-model="form.numberOfRooms"
            type="number"
            min="0"
            step="1"
            placeholder="es. 10"
          />
        </div>
        <!-- Rendita mensile attesa: non più richiesta in creazione (la rendita
             è calcolata dalle rette). Resta modificabile in modifica struttura. -->
        <div v-if="modal.mode === 'edit'" class="form-field">
          <label>Rendita mensile attesa (€)</label>
          <input
            v-model="form.expectedMonthlyRevenue"
            type="number"
            min="0"
            step="any"
          />
        </div>
        <div class="form-field form-field--full">
          <label>Note</label>
          <textarea v-model.trim="form.notes" rows="2"></textarea>
        </div>
      </div>
      <div class="form-actions">
        <button class="villa-btn villa-btn--ghost" @click="closeModal">
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--primary"
          :disabled="modal.busy"
          @click="submit"
        >
          {{
            modal.busy
              ? "Salvataggio…"
              : modal.mode === "edit"
              ? "Salva"
              : "Crea"
          }}
        </button>
      </div>
    </VueFinalModal>

    <!-- Conferma archiviazione -->
    <VueFinalModal v-model="confirm.open" content-class="villa-confirm">
      <h3 class="villa-confirm__title">Archivia struttura</h3>
      <p class="villa-confirm__text">
        Vuoi archiviare <strong>{{ confirm.name }}</strong
        >? Non sarà più visibile in elenco.
      </p>
      <div class="villa-confirm__actions">
        <button
          class="villa-btn villa-btn--ghost"
          :disabled="confirm.busy"
          @click="confirm.open = false"
        >
          Annulla
        </button>
        <button
          class="villa-btn villa-btn--danger"
          :disabled="confirm.busy"
          @click="confirmArchive"
        >
          {{ confirm.busy ? "Archivia…" : "Archivia" }}
        </button>
      </div>
    </VueFinalModal>

    <!-- Conferma custom uscita con modifiche non salvate (mixin discardGuard). -->
    <DiscardChangesModal
      :open="discardGuard.open"
      @stay="onDiscardStay"
      @discard="onDiscardConfirm"
    />
  </section>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DiscardChangesModal from "@/components/DiscardChangesModal.vue";
import discardGuard from "@/mixins/discardGuard";

const TYPES = [
  { value: "residential_housing", label: "Alloggio residenziale" },
  { value: "retirement_home", label: "Casa di riposo" },
  { value: "protected_home", label: "Casa protetta" },
  { value: "rsa", label: "RSA" },
  { value: "community_housing", label: "Comunità alloggio" },
  { value: "other", label: "Altro" },
];

const emptyForm = () => ({
  name: "",
  type: "retirement_home",
  isActive: true,
  code: "",
  legalName: "",
  address: "",
  city: "",
  province: "",
  cap: "",
  phone: "",
  email: "",
  pec: "",
  taxCode: "",
  vatNumber: "",
  codiceUnivoco: "",
  maxCapacity: "0",
  expectedMonthlyFee: "0",
  expectedMonthlyRevenue: "0",
  // Solo creazione: genera le camere vuote corrispondenti. Non persistito.
  numberOfRooms: "",
  notes: "",
});

export default {
  name: "FacilitiesPanel",
  components: { DiscardChangesModal },
  mixins: [discardGuard],
  emits: ["changed"],
  data() {
    return {
      facilities: [],
      loading: true,
      error: "",
      types: TYPES,
      form: emptyForm(),
      // Snapshot del form all'apertura, per il dirty-check in chiusura modale.
      formBaseline: "",
      modal: { open: false, mode: "create", busy: false, id: null },
      confirm: { open: false, busy: false, id: null, name: "" },
    };
  },
  computed: {
    ...mapGetters(["canViewEconomics", "canViewCompany"]),
  },
  methods: {
    ...mapActions([
      "fetchFacilities",
      "createFacility",
      "updateFacility",
      "archiveFacility",
    ]),
    typeLabel(v) {
      const f = TYPES.find((t) => t.value === v);
      return f ? f.label : v;
    },
    eco(f) {
      return f.economics || {};
    },
    occ(f) {
      return f.occupancy || {};
    },
    euro(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return `€ ${n.toLocaleString("it-IT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    async load() {
      this.loading = true;
      this.error = "";
      try {
        this.facilities = await this.fetchFacilities();
      } catch (err) {
        this.error =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Errore nel caricamento delle strutture.";
      } finally {
        this.loading = false;
      }
    },
    openFacility(f) {
      this.$router.push(`/facilities/${f.id}`);
    },
    openCreate() {
      this.form = emptyForm();
      this.modal = { open: true, mode: "create", busy: false, id: null };
      this.formBaseline = JSON.stringify(this.form);
    },
    // Chiusura richiesta dal bottone Annulla. Passa dalla guardia condivisa.
    closeModal() {
      this.requestClose(JSON.stringify(this.form) !== this.formBaseline, () => {
        this.modal.open = false;
      });
    },
    // Intercetta le chiusure di vue-final-modal (click fuori + ESC, riabilitati):
    // se ci sono modifiche non salvate mostra la conferma custom, altrimenti
    // chiude. Le aperture (val=true) mantengono aperta la modale.
    onModalToggle(val) {
      if (val) {
        this.modal.open = true;
        return;
      }
      this.requestClose(JSON.stringify(this.form) !== this.formBaseline, () => {
        this.modal.open = false;
      });
    },
    openEdit(f) {
      this.form = {
        name: f.name || "",
        type: f.type || "retirement_home",
        isActive: !!f.isActive,
        code: f.code || "",
        legalName: f.legalName || "",
        address: f.address || "",
        city: f.city || "",
        province: f.province || "",
        cap: f.cap || "",
        phone: f.phone || "",
        email: f.email || "",
        pec: f.pec || "",
        taxCode: f.taxCode || "",
        vatNumber: f.vatNumber || "",
        codiceUnivoco: f.codiceUnivoco || "",
        maxCapacity: f.maxCapacity != null ? String(f.maxCapacity) : "0",
        expectedMonthlyFee:
          f.expectedMonthlyFee != null
            ? String(Number(f.expectedMonthlyFee))
            : "0",
        expectedMonthlyRevenue:
          f.expectedMonthlyRevenue != null
            ? String(Number(f.expectedMonthlyRevenue))
            : "0",
        numberOfRooms: "",
        notes: f.notes || "",
      };
      this.modal = { open: true, mode: "edit", busy: false, id: f.id };
      this.formBaseline = JSON.stringify(this.form);
    },
    buildPayload() {
      const num = (v) => (v === "" ? 0 : Number(v));
      return {
        name: this.form.name,
        type: this.form.type,
        isActive: this.form.isActive,
        code: this.form.code || null,
        legalName: this.form.legalName || null,
        address: this.form.address || null,
        city: this.form.city || null,
        province: this.form.province || null,
        cap: this.form.cap || null,
        phone: this.form.phone || null,
        email: this.form.email || null,
        pec: this.form.pec || null,
        taxCode: this.form.taxCode || null,
        vatNumber: this.form.vatNumber || null,
        codiceUnivoco: this.form.codiceUnivoco || null,
        maxCapacity: num(this.form.maxCapacity),
        expectedMonthlyFee: num(this.form.expectedMonthlyFee),
        expectedMonthlyRevenue: num(this.form.expectedMonthlyRevenue),
        // Solo in creazione: genera le camere vuote. Ignorato in modifica lato
        // backend (non è tra i campi aggiornabili di Facility).
        numberOfRooms: this.form.numberOfRooms,
        notes: this.form.notes || null,
      };
    },
    validate(payload) {
      if (!payload.name) return "Il nome della struttura è obbligatorio.";
      for (const [k, label] of [
        ["maxCapacity", "La capienza massima"],
        ["expectedMonthlyFee", "La retta mensile attesa"],
        ["expectedMonthlyRevenue", "La rendita mensile attesa"],
      ]) {
        if (!Number.isFinite(payload[k]) || payload[k] < 0) {
          return `${label} deve essere un numero maggiore o uguale a 0.`;
        }
      }
      if (this.modal.mode === "create" && this.form.numberOfRooms !== "") {
        const rooms = Number(this.form.numberOfRooms);
        if (!Number.isInteger(rooms) || rooms < 0) {
          return "Il numero di camere deve essere un intero maggiore o uguale a 0.";
        }
      }
      return "";
    },
    async submit() {
      const payload = this.buildPayload();
      const validationError = this.validate(payload);
      if (validationError) return this.$toast.error(validationError);

      this.modal.busy = true;
      try {
        if (this.modal.mode === "edit") {
          await this.updateFacility({ facilityId: this.modal.id, payload });
          this.$toast.success("Struttura aggiornata.");
        } else {
          await this.createFacility(payload);
          this.$toast.success("Struttura creata.");
        }
        this.modal.open = false;
        await this.load();
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Salvataggio struttura non riuscito."
        );
      } finally {
        this.modal.busy = false;
      }
    },
    askArchive(f) {
      this.confirm = { open: true, busy: false, id: f.id, name: f.name };
    },
    async confirmArchive() {
      this.confirm.busy = true;
      try {
        await this.archiveFacility(this.confirm.id);
        this.$toast.success("Struttura archiviata.");
        this.confirm.open = false;
        await this.load();
        this.$emit("changed");
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Archiviazione non riuscita."
        );
      } finally {
        this.confirm.busy = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style src="@/css/AppLayout.css" />
